import React, { useState, useRef, useEffect } from 'react'; 
import {
  Box,
  Typography,
  Button,
  IconButton,
  Tabs,
  Tab,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from '@mui/material';
import PhotoCamera from '@mui/icons-material/PhotoCamera';
import CloseIcon from '@mui/icons-material/Close';
import theme from '../../../../theme';
import { useTranslation } from 'react-i18next';

// Define the options outside the component for clarity
const interiorOptions = [
  'Tear on the seat',
  'Stains',
  'Strong smell',
  'Damaged entertainment system',
  'Controls not working',
  'Others',
];

const exteriorOptions = [
  'Front bumper',
  'Hood',
  'Headlight',
  'Fog light',
  'Grille',
  'Windshield',
  'Fender',
  'Side mirror',
  'Rocker panel',
  'Running board',
  'Rear quarter panel',
  'Rear bumper',
  'Trunk',
  'Taillight',
  'Roof',
  'Warning lights',
  'Tires',
  'Rims',
  'Others',
];

// Helper component for Tab Panel content
const TabPanel = (props) => {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 1 }}>
          {children}
        </Box>
      )}
    </div>
  );
};


const DamagePhotosModal = ({ 
  isEditingDesc,
  desc,
  img,
  onClose,
  onSubmit
}) => {
  
  const { t } = useTranslation('myGarage');
  
  // 1. Ref to measure the width of the input field
  const formControlRef = useRef(null); 
  // 2. State to hold the measured width
  const [menuWidth, setMenuWidth] = useState(700); 

  const [image, setImage] = useState(null);
  const [selectedTab, setSelectedTab] = useState(0); 
  const [selectedLabel, setSelectedLabel] = useState('');

  // Determine initial tab based on initial label (desc prop)
  useEffect(() => {
    if (desc) {
      setSelectedLabel(desc);
      if (exteriorOptions.includes(desc)) {
        setSelectedTab(0);
      } else if (interiorOptions.includes(desc)) {
        setSelectedTab(1);
      }
    }
    if (img && !isEditingDesc) {
      setImage(img);
    }
  }, [isEditingDesc, desc, img]);

  // 3. Measure the width of the FormControl and store it
  useEffect(() => {
    if (formControlRef.current) {
      // Get the client width of the FormControl element (which is fullWidth)
      setMenuWidth(formControlRef.current.clientWidth);
    }
  }, [selectedTab]); 


  // 4. Define MenuProps using the measured width
  const menuProps = {
    PaperProps: {
      style: {
        width: menuWidth,
        maxHeight: 300,
      },
      sx: {
        minWidth: menuWidth, 
      }
    },
    anchorOrigin: {
      vertical: 'bottom',
      horizontal: 'left',
    },
    transformOrigin: {
      vertical: 'top',
      horizontal: 'left',
    },
    getContentAnchorEl: null,
  };


  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setImage(file);
    }
  };

  const handleTabChange = (event, newValue) => {
    setSelectedTab(newValue);
  };

  const handleLabelChange = (event) => {
    setSelectedLabel(event.target.value);
  };

  const handleClose = () => {
    setImage(null);
    setSelectedLabel('');
    setSelectedTab(0);
    onClose();
  };

  const isSubmitDisabled = !isEditingDesc ? (!image || !selectedLabel) : !selectedLabel;


  return (
    <Box
      sx={{
        width: 400,
        bgcolor: 'background.paper',
        p: 4,
        borderRadius: 2,
        boxShadow: 24,
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 2,
      }}
    >
      <IconButton
        onClick={handleClose}
        sx={{
          position: 'absolute',
          top: 8,
          right: 8,
        }}
      >
        <CloseIcon />
      </IconButton>

      {!isEditingDesc && (
        <Typography variant="h6" component="h2" sx={{ mb: 1 }}>
          {t("Upload Damage Photo")}
        </Typography>
      )}

      {!isEditingDesc && (
        <Box
          sx={{
            width: '100%',
            height: 200,
            border: '2px dashed #ccc',
            borderRadius: 1,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            position: 'relative',
            overflow: 'hidden',
          }}
        >
          {image ? (
            <Box
              component="img"
              src={typeof image === 'string' ? image : URL.createObjectURL(image)}
              alt="Uploaded Preview"
              sx={{
                width: '100%',
                height: '100%',
                objectFit: 'cover',
              }}
            />
          ) : (
            <>
              <PhotoCamera sx={{ fontSize: 40, color: "text.primary", mb: 1 }} />
              <Typography variant="body1" sx={{ color: 'text.primary' }}>
                {t("Upload Image")}
              </Typography>
            </>
          )}
          <Button
            variant="contained"
            component="label"
            sx={{
              position: 'absolute',
              bottom: 8,
              left: '50%',
              transform: 'translateX(-50%)',
              bgcolor: 'primary.main',
              '&:hover': { bgcolor: 'primary.dark' },
            }}
          >
            {t("Choose File")}
            <input
              type="file"
              hidden
              accept="image/*"
              onChange={handleImageChange}
            />
          </Button>
        </Box>
      )}

      {/* Tabs for Interior/Exterior */}
      <Box sx={{ width: '100%', borderBottom: 1, borderColor: 'divider', mt: 1 }}>
        <Tabs value={selectedTab} onChange={handleTabChange} aria-label="damage location tabs" centered>
          <Tab 
            sx={{
              color: theme.palette.text.primary,
            }} label={t("Exterior")} id="simple-tab-0" aria-controls="simple-tabpanel-0" />
          <Tab label={t("Interior")} 
            sx={{ 
              color: theme.palette.text.primary }} id="simple-tab-1" aria-controls="simple-tabpanel-1" />
        </Tabs>
      </Box>

      {selectedTab === 0 ? (
        <Box sx={{ width: "100% !important" }}>
          <FormControl fullWidth sx={{ minWidth: 120 }} ref={formControlRef}> 
            <InputLabel id="exterior-damage-select-label">{t("Select Exterior Damage")}</InputLabel>
            <Select
              MenuProps={menuProps}
              labelId="exterior-damage-select-label"
              value={selectedTab === 0 ? selectedLabel : ''}
              label={t("Select Exterior Damage")}
              onChange={handleLabelChange}
            >
              {exteriorOptions.map((option) => (
                <MenuItem key={option} value={option}>
                  {t(`options.${option}`)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      ) : null}
      
      {selectedTab === 1 ? (
        <Box sx={{ width: "100% !important" }}>
          <FormControl fullWidth sx={{ minWidth: 120 }}>
            <InputLabel id="interior-damage-select-label">{t("Select Interior Damage")}</InputLabel>
            <Select
              MenuProps={menuProps}
              labelId="interior-damage-select-label"
              value={selectedTab === 1 ? selectedLabel : ''}
              label={t("Select Interior Damage")}
              onChange={handleLabelChange}
            >
              {interiorOptions.map((option) => (
                <MenuItem key={option} value={option}>
                  {t(`options.${option}`)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      ) : null}

      <Button
        variant="contained"
        onClick={() => {
          if (onSubmit) {
            onSubmit({
              image,
              description: selectedLabel,
              location: selectedTab === 0 ? 'Exterior' : 'Interior',
            });
          }
          handleClose();
        }}
        disabled={isSubmitDisabled}
        sx={{ mt: 2 }}
      >
        {t("Submit")}
      </Button>
    </Box>
  );
};

export default DamagePhotosModal;